/**
 * コンテナステータステーブルコンポーネント
 *
 * Dockerコンテナの状態を表形式で表示
 */

import React from 'react';
import { Table, Tag, Progress } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { STATUS_COLORS, THEME_COLORS } from '@/shared/constants/colors';
import type { ContainerStatus, ContainerStatusValue } from '../types/serverStatus.types';

// ===== 定数 =====

const CONTAINER_TABLE_CONSTANTS = {
  /** メモリ使用率の警告閾値（%） */
  MEMORY_WARNING_THRESHOLD: 70,
  /** メモリ使用率の危険閾値（%） */
  MEMORY_CRITICAL_THRESHOLD: 85,
  /** パーセント計算用 */
  PERCENTAGE_MULTIPLIER: 100,
} as const;

const STATUS_CONFIG: Record<ContainerStatusValue, { color: string; text: string }> = {
  running: { color: STATUS_COLORS.ACTIVE, text: '稼働中' },
  stopped: { color: STATUS_COLORS.ERROR, text: '停止' },
  restarting: { color: STATUS_COLORS.WARNING, text: '再起動中' },
  unknown: { color: STATUS_COLORS.STOPPED, text: '不明' },
};

// ===== Props =====

interface ContainerStatusTableProps {
  readonly containers: readonly ContainerStatus[];
  readonly isMobile: boolean;
  readonly loading: boolean;
}

// ===== ヘルパー関数 =====

/**
 * メモリ使用率を計算
 */
const calculateMemoryPercent = (used: number, limit: number): number => {
  if (limit === 0) {
    return 0;
  }
  return (used / limit) * CONTAINER_TABLE_CONSTANTS.PERCENTAGE_MULTIPLIER;
};

/**
 * メモリ使用率に応じたプログレスバーの色を取得
 */
const getMemoryProgressColor = (percent: number): string => {
  if (percent >= CONTAINER_TABLE_CONSTANTS.MEMORY_CRITICAL_THRESHOLD) {
    return STATUS_COLORS.ERROR;
  }
  if (percent >= CONTAINER_TABLE_CONSTANTS.MEMORY_WARNING_THRESHOLD) {
    return STATUS_COLORS.WARNING;
  }
  return THEME_COLORS.SUCCESS;
};

/**
 * コンテナ名から表示名を生成（faro-プレフィックスを除去）
 */
const getDisplayName = (name: string): string => {
  const FARO_PREFIX = 'faro-';
  if (name.startsWith(FARO_PREFIX)) {
    return name.substring(FARO_PREFIX.length);
  }
  return name;
};

// ===== コンポーネント =====

export const ContainerStatusTable: React.FC<ContainerStatusTableProps> = ({
  containers,
  isMobile,
  loading,
}) => {
  const columns: ColumnsType<ContainerStatus> = [
    {
      title: 'コンテナ',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <span style={{ fontWeight: 500 }}>{getDisplayName(name)}</span>
      ),
      width: isMobile ? 100 : 150,
    },
    {
      title: '状態',
      dataIndex: 'status',
      key: 'status',
      render: (status: ContainerStatusValue) => {
        const config = STATUS_CONFIG[status];
        return (
          <Tag color={config.color} style={{ margin: 0 }}>
            {config.text}
          </Tag>
        );
      },
      width: 90,
    },
    {
      title: 'メモリ',
      key: 'memory',
      render: (_, record: ContainerStatus) => {
        const percent = calculateMemoryPercent(record.memoryUsedMB, record.memoryLimitMB);
        const displayText = record.memoryLimitMB > 0
          ? `${record.memoryUsedMB}/${record.memoryLimitMB} MB`
          : `${record.memoryUsedMB} MB`;

        if (record.memoryLimitMB === 0) {
          return <span>{displayText}</span>;
        }

        return (
          <div style={{ minWidth: isMobile ? 80 : 120 }}>
            <Progress
              percent={Math.round(percent)}
              size="small"
              strokeColor={getMemoryProgressColor(percent)}
              format={() => `${Math.round(percent)}%`}
            />
            <span style={{ fontSize: 11, color: '#888' }}>{displayText}</span>
          </div>
        );
      },
      width: isMobile ? 100 : 160,
    },
    {
      title: 'CPU',
      dataIndex: 'cpuPercent',
      key: 'cpuPercent',
      render: (cpuPercent: number) => `${cpuPercent.toFixed(1)}%`,
      width: 70,
      responsive: isMobile ? undefined : ['md'],
    },
  ];

  // モバイルではCPU列を除外
  const mobileColumns = isMobile
    ? columns.filter(col => col.key !== 'cpuPercent')
    : columns;

  return (
    <Table<ContainerStatus>
      columns={mobileColumns}
      dataSource={containers.map((container, index) => ({
        ...container,
        key: container.name || index,
      }))}
      pagination={false}
      size="small"
      loading={loading}
      scroll={isMobile ? { x: 'max-content' } : undefined}
    />
  );
};
